/**
 * «کمک برای همین صفحه» — two or three short lines per section of the panel,
 * for the pages the tours do not cover (or a viewer the page tour is not for).
 * The Help menu offers them as its first item when the page has no tour, and
 * a help offer on such a page points here instead of at a tour (critic C6).
 *
 * Keyed by `SECTIONS[].key` (`app/sections.ts`); `help-notes.test.ts` fails
 * when a section has neither a page tour nor a note. Every line states what
 * the screen really does — no promises it does not keep — and follows the
 * guidance copy rules (swept by the same test).
 */
export interface HelpNote {
  title: string;
  bullets: readonly string[];
}

export const HELP_NOTES: Readonly<Record<string, HelpNote>> = {
  dashboard: {
    title: 'داشبورد',
    bullets: [
      'نوبت‌های امروز و کارهایی که باید پیگیری کنید، همین‌جاست.',
      'با «ثبت نوبت» در بالای صفحه، بدون رفتن به تقویم نوبت بدید.',
      'پیشنهادهای بوکورا رو هم همین‌جا می‌بینید؛ همهٔ پیشنهادها در «راهنما و پیشنهادها» در دسترس‌اند.',
    ],
  },
  patients: {
    title: 'بیماران',
    bullets: [
      'بیمار رو با نام، موبایل، کد ملی یا شمارهٔ پرونده پیدا کنید.',
      'با «ثبت بیمار جدید» پرونده بسازید؛ فهرست قبلی بیماران رو با «ثبت از فایل اکسل» وارد کنید.',
      'روی هر بیمار بزنید تا پرونده‌اش باز بشه: نوبت‌ها، جلسه‌های درمان، عکس‌ها و حساب مالی.',
    ],
  },
  calendar: {
    title: 'تقویم نوبت‌ها',
    bullets: [
      'روی یک ساعت خالی بزنید تا فرم ثبت نوبت برای همون ساعت باز بشه.',
      'با زدن روی هر نوبت، جزئیاتش رو ببینید، وضعیتش رو عوض کنید یا لغوش کنید.',
    ],
  },
  sessions: {
    title: 'جلسه‌های درمان',
    bullets: [
      'جلسه درمان رو از پرونده بیمار یا از روی نوبت ثبت کنید.',
      'مواد مصرفی جلسه درمان از انبار کسر می‌شه. در پلن نتیجه‌محور، هزینهٔ هر بیمار هم بر اساس جلسه‌های درمان ثبت‌شده محاسبه می‌شه.',
      'بعد از انجام جلسهٔ برنامه‌ریزی‌شده، «نهایی کردن» رو بزنید.',
    ],
  },
  photos: {
    title: 'عکس‌های قبل و بعد',
    bullets: [
      'عکس‌های بیمار در پرونده‌اش می‌مونه و لینک عمومی نداره.',
      'در پنل کلینیک، دیدن عکس‌ها ثبت می‌شه و فقط کارکنان دارای سطح دسترسی مربوط می‌تونن عکس‌ها رو ببینن.',
    ],
  },
  inventory: {
    title: 'انبار',
    bullets: [
      'با «ثبت ورود کالا» موجودی و تاریخ انقضای هر سری ساخت رو ثبت کنید.',
      'مصرف جلسه‌های درمان خودکار از موجودی کم می‌شه.',
      'کالاهای رو به اتمام یا نزدیک به انقضا در همین صفحه مشخص‌اند.',
    ],
  },
  packages: {
    title: 'بسته‌های خدمات',
    bullets: [
      'بسته خدمات یعنی چند جلسه درمان از یک یا چند خدمت با یک قیمت.',
      'بسته‌ای که به بیمار می‌فروشید در پرونده‌اش ثبت می‌شه. هر بار جلسه درمان رو از اون بسته ثبت کنید، از جلسه‌های باقی‌مانده کم می‌شه.',
    ],
  },
  crm: {
    title: 'درخواست‌های مشاوره و پیامک',
    bullets: [
      'درخواست‌های مشاوره رو پیگیری کنید. وقتی فرد برای دریافت خدمات آماده شد، براش پرونده بیمار بسازید.',
      'گروه بیماران بسازید و براشون پیامک گروهی بفرستید.',
      'پیامک گروهی فقط بعد از تأیید شما ارسال می‌شه.',
    ],
  },
  insights: {
    title: 'بینش‌ها',
    bullets: [
      'چند نفر صفحهٔ عمومی کلینیک رو دیدن، فرم نوبت رو باز کردن و درخواست فرستادن، همین‌جاست.',
      'هر مرحله جداگانه شمرده می‌شه؛ درصد هر ردیف نسبت به مرحلهٔ قبله.',
      'با زدن «درخواست آنلاین» یا «تأیید شده»، همون درخواست‌ها رو در تقویم ببینید.',
    ],
  },
  reviews: {
    title: 'نظرات بیماران',
    bullets: [
      'نظرهای بیماران و میانگین امتیاز کلینیک همین‌جاست.',
      'به هر نظر می‌تونید پاسخ عمومی بدید.',
    ],
  },
  till: {
    title: 'صندوق',
    bullets: [
      'دریافت‌های امروز و نقدی که باید در صندوق باشه همین‌جاست.',
      'آخر روز یا شیفت، پول رو بشمرید و «بستن صندوق» رو بزنید؛ اختلاف و تحویل‌گیرنده ثبت می‌شه.',
      'هر دریافتی که جای دیگه‌ای ثبت بشه، خودکار در صندوق دیده می‌شه.',
    ],
  },
  accounting: {
    title: 'حسابداری',
    bullets: [
      'دریافت‌ها، هزینه‌ها و سهم پزشکان همین‌جا ثبت و دیده می‌شه.',
      'گزارش سود و زیان بر اساس جلسات درمان انجام‌شده، هزینه‌ها و حقوق ثبت‌شده محاسبه می‌شه.',
    ],
  },
  procedures: {
    title: 'خدمات و تعرفه‌ها',
    bullets: [
      'خدماتی رو که ارائه می‌دید، با قیمت و مدت، اینجا تعریف کنید.',
      'همین فهرست در ثبت نوبت و جلسه درمان دیده می‌شه.',
      'اگر خدمتی در فهرست خدمات بوکورا نیست، خودتون بسازیدش.',
    ],
  },
  staff: {
    title: 'کارکنان و نقش‌ها',
    bullets: [
      'نقش هر نفر مشخص می‌کنه به کدوم بخش‌ها دسترسی داره و چه کارهایی می‌تونه انجام بده.',
      'با «دعوت کارمند» شماره موبایلش رو بدید؛ با همون شماره وارد پنل می‌شه.',
      'ساعت کاری هر پزشک در مشخصات همون پزشک تنظیم می‌شه.',
    ],
  },
  settings: {
    title: 'تنظیمات',
    bullets: [
      'مشخصات کلینیک، ساعات کاری، اتاق‌ها و اشتراک همین‌جاست.',
      'مراحل راه‌اندازی کلینیک رو در تب «راه‌اندازی» می‌بینید.',
    ],
  },
};

export function helpNoteFor(section: string | null | undefined): HelpNote | null {
  if (!section) return null;
  return HELP_NOTES[section] ?? null;
}
